/**
 * Utilidades transversales de presentación.
 */

import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Combina clases de Tailwind resolviendo conflictos.
 * `cn('p-2', 'p-4')` -> 'p-4'
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

const CURRENCY_FORMATTER = new Intl.NumberFormat('es-PE', {
  style: 'currency',
  currency: 'PEN',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const NUMBER_FORMATTER = new Intl.NumberFormat('es-PE', {
  maximumFractionDigits: 2,
});

const VOLUME_FORMATTER = new Intl.NumberFormat('es-PE', {
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
});

const COMPACT_FORMATTER = new Intl.NumberFormat('es-PE', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

export const formatCurrency = (value: number | null | undefined): string =>
  CURRENCY_FORMATTER.format(Number(value ?? 0));

export function formatPaymentMethodName(value: string | null | undefined): string {
  const nombre = String(value ?? '').trim().toLocaleLowerCase('es-PE');

  return nombre
    ? `${nombre.charAt(0).toLocaleUpperCase('es-PE')}${nombre.slice(1)}`
    : 'Sin método';
}

/**
 * Monto abreviado para espacios estrechos: `S/ 350 K`, `S/ 1.2 M`.
 * Se usa donde no cabe el importe completo (centro de una dona, tarjetas).
 */
export const formatCurrencyCompact = (value: number | null | undefined): string => {
  const numero = Number(value ?? 0);

  if (!Number.isFinite(numero)) return CURRENCY_FORMATTER.format(0);
  if (Math.abs(numero) < 1000) return CURRENCY_FORMATTER.format(numero);

  // Intl genera el sufijo en minúscula (`350 k`); se normaliza a mayúscula.
  return `S/ ${COMPACT_FORMATTER.format(numero).replace(/\s?k$/i, ' K')}`;
};

/**
 * Monto adaptado al tamaño: en millones se abrevia (`S/ 1.2 M`) y por debajo se
 * muestra completo con céntimos (`S/ 350,000.00`).
 *
 * Una estación de servicio factura cientos de miles o millones al mes, y el
 * importe completo no cabe en tarjetas y celdas sin romper la maquetación.
 * El valor exacto se ofrece en el `title` del elemento que lo muestra.
 */
export function formatCurrencyAdaptive(
  value: number | null | undefined,
  umbral = 1_000_000,
): string {
  const numero = Number(value ?? 0);

  if (!Number.isFinite(numero)) return CURRENCY_FORMATTER.format(0);

  return Math.abs(numero) >= umbral ? formatCurrencyCompact(numero) : CURRENCY_FORMATTER.format(numero);
}

export const formatNumber = (value: number | null | undefined): string =>
  NUMBER_FORMATTER.format(Number(value ?? 0));

/**
 * Volúmenes y contómetros del surtidor: **siempre tres decimales**.
 *
 * Los contadores vienen en milésimas y el panel anterior los mostraba con tres
 * decimales (`70.330`); con el formateador normal se perdería el último dígito
 * de la diferencia entre dos contómetros.
 */
export const formatVolumen = (value: number | null | undefined): string =>
  VOLUME_FORMATTER.format(Number(value ?? 0));

export const formatCompact = (value: number | null | undefined): string =>
  COMPACT_FORMATTER.format(Number(value ?? 0));

export const toNumber = (value: unknown): number => {
  const parsed = typeof value === 'number' ? value : Number.parseFloat(String(value ?? 0));
  return Number.isFinite(parsed) ? parsed : 0;
};

/** Convierte un timestamp Unix (segundos) a fecha local legible. */
export function formatUnixDate(seconds: number | null | undefined): string {
  if (!seconds) return '—';

  const date = new Date(seconds * 1000);
  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleString('es-PE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const FECHA_SIMPLE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Convierte un valor de fecha a `Date`.
 *
 * `new Date('2026-07-01')` se interpreta como UTC y en Perú (UTC-5) termina
 * mostrando el 30/06. Las fechas sin hora (`YYYY-MM-DD`, las de los selectores)
 * se construyen con componentes locales para que se vean tal como se eligieron.
 */
function parseDateValue(value: string | Date): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const simple = FECHA_SIMPLE.exec(value.trim());

  if (simple) {
    return new Date(Number(simple[1]), Number(simple[2]) - 1, Number(simple[3]));
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

const FECHA_HORA = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/;

/**
 * Convierte fecha-hora a `Date`.
 *
 * Las fechas del backend sin zona (`2026-02-01 06:02:29`, como las ventanas de
 * turno) se interpretan como hora local: el navegador no las parsea igual en
 * todos los motores, así que se construyen a mano.
 */
function parseDateTimeValue(value: string | Date): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const texto = value.trim();
  const propia = FECHA_HORA.exec(texto);

  if (propia) {
    return new Date(
      Number(propia[1]),
      Number(propia[2]) - 1,
      Number(propia[3]),
      Number(propia[4]),
      Number(propia[5]),
      Number(propia[6] ?? 0),
    );
  }

  return parseDateValue(texto);
}

/** Formatea fecha y hora local (ej. 01/02/2026 06:02). */
export function formatDateTimeValue(
  value: string | Date | null | undefined,
  conSegundos = false,
): string {
  if (!value) return '—';

  const date = parseDateTimeValue(value);
  if (!date) return '—';

  return date.toLocaleString('es-PE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...(conSegundos ? { second: '2-digit' as const } : {}),
  });
}

/** Hora local `HH:mm` (para las ventanas de turno del consolidado). */
export function formatHoraValue(value: string | Date | null | undefined): string {
  if (!value) return '—';

  const date = parseDateTimeValue(value);
  if (!date) return '—';

  return date.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** Formatea una fecha ISO / Date a formato corto local (ej. 06/04/2027). */
export function formatDateValue(value: string | Date | null | undefined): string {
  if (!value) return '—';

  const date = parseDateValue(value);
  if (!date) return '—';

  return date.toLocaleDateString('es-PE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** Fecha en formato YYYY-MM-DD (formato que espera la API). */
export function toIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Iniciales para avatares: "JOSE CARLOS SILVA" -> "JS". */
export function initials(name: string | null | undefined): string {
  if (!name) return '?';

  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';

  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';

  return `${first}${last}`.toUpperCase();
}

/** Espera asíncrona (usada entre reintentos de sesión). */
export const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
